import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import {
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  isValid,
  startOfDay,
} from "date-fns";
import type { VaccinationsReceivedMap } from "./provider";
import {
  TimeUnit,
  type VaccinationSchedule,
  type VaccinationScheduleDisease,
  type VaccinationScheduleDose,
  type VaccineCode,
  vaccinationScheduleTemplate,
} from "./vaccination-schedule";

const TimeUnitToDateFunctionMapper: Record<
  TimeUnit,
  (date: Date, amount: number) => Date
> = {
  [TimeUnit.WEEKS]: addWeeks,
  [TimeUnit.MONTHS]: addMonths,
  [TimeUnit.YEARS]: addYears,
};

export const calculateVaccinationDoseDates = ({
  completedDate,
  currentDose,
  prevDose,
  patientDob,
  setUnusualVaccines,
  completingCode,
}: {
  completedDate: Date | null;
  currentDose: VaccinationScheduleDose;
  prevDose: VaccinationScheduleDose | null;
  patientDob: Date;
  setUnusualVaccines: ((code: VaccineCode) => void) | null;
  /**
   *  the code of the vaccine that was last given *
   */
  completingCode: VaccineCode;
}) => {
  const missingDate = TimeUnitToDateFunctionMapper[
    currentDose.missingAfterDobTimeUnit
  ](patientDob, currentDose.missingAfterDobQuantity);

  const dueDate = startOfDay(
    TimeUnitToDateFunctionMapper[currentDose.scheduledAfterPreviousTimeUnit](
      currentDose.calculateFromPrevDose ? prevDose?.completedDate : patientDob,
      currentDose.scheduledAfterPreviousQuantity,
    ),
  );

  if (completedDate) {
    if (setUnusualVaccines && differenceInDays(dueDate, completedDate) >= 7) {
      setUnusualVaccines(completingCode);
    }
    return { completedDate, dueDate: null, missingDate: null };
  }

  if (currentDose.calculateFromPrevDose && !prevDose?.completedDate) {
    return { completedDate: null, dueDate: null, missingDate };
  }
  return {
    completedDate: null,
    dueDate,
    missingDate,
  };
};

export const calculateNextAvailableVaccineDate = (
  codes: VaccinationScheduleDose["codes"],
  datesMap: VaccinationsReceivedMap,
  usedDatesPerDisease: Map<string, Set<string>>,
): { completedDate: Date | null; completingCode: VaccineCode | null } => {
  let selectedDate: Date | null = null;
  let selectedCode: string | null = null;

  for (const code of codes) {
    if (datesMap[code] && datesMap[code].length > 0) {
      const availableDates = datesMap[code].filter(
        (date) =>
          isValid(date) &&
          !usedDatesPerDisease.get(code)?.has(date.toISOString()),
      );

      if (availableDates.length > 0) {
        selectedDate = availableDates[0];
        selectedCode = code;
        break;
      }
    }
  }

  if (selectedDate && selectedCode && isValid(selectedDate)) {
    if (!usedDatesPerDisease.has(selectedCode)) {
      usedDatesPerDisease.set(selectedCode, new Set());
    }
    usedDatesPerDisease.get(selectedCode).add(selectedDate.toISOString());
  }

  return { completedDate: selectedDate, completingCode: selectedCode };
};

/**
 * Calculates an immunisation schedule for a patient, applying existing and pending vaccinations
 *
 * @param patient The patient record
 * @param vaccinations Map of vaccination codes to dates received
 * @param baseSchedule Base schedule template (default: immunisationScheduleTemplate)
 * @returns The calculated immunisation schedule
 */
export const calculatePatientVaccinationSchedule = ({
  patient,
  vaccinations,
  baseSchedule = vaccinationScheduleTemplate,
  setUnusualVaccines = null,
}: {
  patient: Patient | null;
  vaccinations: VaccinationsReceivedMap;
  baseSchedule?: VaccinationSchedule;
  setUnusualVaccines?: ((completingCode: VaccineCode) => void) | null;
}): VaccinationSchedule => {
  if (!patient || !vaccinations) {
    return baseSchedule;
  }
  return Object.fromEntries(
    Object.entries(baseSchedule).map(([disease, data]) => {
      const updatedDoses: VaccinationScheduleDose[] = [];
      const usedDatesPerDisease = new Map<string, Set<string>>();

      return [
        disease as VaccinationScheduleDisease,
        {
          ...data,
          doses: data.doses.map((dose, idx) => {
            const prevDose = updatedDoses[idx - 1] || null;

            if (dose.completedDate !== null) {
              const alreadyCompletedDose = { ...dose };
              updatedDoses.push(alreadyCompletedDose);
              return alreadyCompletedDose;
            }
            const { completedDate, completingCode } =
              calculateNextAvailableVaccineDate(
                dose.codes,
                vaccinations,
                usedDatesPerDisease,
              );

            const updatedDose = {
              ...dose,
              ...calculateVaccinationDoseDates({
                completedDate,
                currentDose: dose,
                prevDose,
                patientDob: new Date(patient.dob),
                setUnusualVaccines,
                completingCode,
              }),
            };

            updatedDoses.push(updatedDose);
            return updatedDose;
          }),
        },
      ];
    }),
  ) as VaccinationSchedule;
};
